import { reactive, watchEffect } from "vue";
import { useBreakpoints } from "@sazka/utils/composables/use-breakpoints";
import {
    CanvasColorConfiguration,
    CanvasConfiguration,
    CanvasTextConfiguration,
    WheelAssetsConfiguration,
    WheelAssetsSizeConfiguration,
    WheelSizeConfiguration,
} from "@sazka/wheel/spinning-wheel/models/sw-configuration";

const colors: CanvasColorConfiguration = {
    segmentBackground: [
        {
            startColor: "#d2e3ff",
            endColor: "#94ACFE",
        },
        {
            startColor: "#ffafaf",
            endColor: "#FB7170",
        },
        {
            startColor: "#ffefb6",
            endColor: "#FFC077",
        },
        {
            startColor: "#f5d8ff",
            endColor: "#CD9BFF",
        },
        {
            startColor: "#edffea",
            endColor: "#BCFFB6",
        },
        {
            startColor: "#48e2ff",
            endColor: "#27A9F4",
        },
        {
            startColor: "#eafcff",
            endColor: "#B6E5FF",
        },
        {
            startColor: "#9fffce",
            endColor: "#63FF8F",
        },
    ],
    mainPrizeBackground: {
        startColor: "#fffc4a",
        endColor: "#ffba01",
    },
    jokerBackground: {
        startColor: "#306C81",
        endColor: "#204E5E",
    },
    textColors: ["#232157"],
};

const { xs1, xs2, sm, sm2, lg } = useBreakpoints("xs1", "xs2", "sm", "sm2", "lg");

/* ******************** WHEEL SIZES **************** */
const wheelSizes = reactive<WheelSizeConfiguration>({
    size: 0,
    radius: 0,
    scaleRatio: window.devicePixelRatio || 1,
});

const setGwWheelSizes = () => {
    if (lg.value) {
        wheelSizes.size = 584;
        wheelSizes.radius = 292;
    } else if (sm.value) {
        wheelSizes.size = 408;
        wheelSizes.radius = 204;
    } else if (xs2.value) {
        wheelSizes.size = 364;
        wheelSizes.radius = 182;
    } else if (xs1.value) {
        wheelSizes.size = 306;
        wheelSizes.radius = 153;
    } else {
        wheelSizes.size = 264;
        wheelSizes.radius = 132;
    }
};

/* ****************** TEXT SIZES ******************** */
const textSizes = reactive<CanvasTextConfiguration>({
    rimOffset: 0,
    rimFontSize: 0,
    innerOffset: 0,
    innerFontSize: 0,
});

const setGwTextSizes = () => {
    if (lg.value) {
        textSizes.rimOffset = 9;
        textSizes.rimFontSize = 12;
        textSizes.innerOffset = 40;
        textSizes.innerFontSize = 25;
    } else if (sm.value) {
        textSizes.rimOffset = 8;
        textSizes.rimFontSize = 8;
        textSizes.innerOffset = 30;
        textSizes.innerFontSize = 17;
    } else if (xs2.value) {
        textSizes.rimOffset = 8;
        textSizes.rimFontSize = 7;
        textSizes.innerOffset = 28;
        textSizes.innerFontSize = 15;
    } else if (xs1.value) {
        textSizes.rimOffset = 7;
        textSizes.rimFontSize = 6;
        textSizes.innerOffset = 23;
        textSizes.innerFontSize = 13;
    } else {
        textSizes.rimOffset = 5;
        textSizes.rimFontSize = 5;
        textSizes.innerOffset = 18;
        textSizes.innerFontSize = 12;
    }
};

const assetsSizes = reactive<WheelAssetsSizeConfiguration>({
    joker: {
        width: 51,
        height: 51,
        textMarginTop: 24,
        assetMarginTop: 0,
    },
});

const setGwJokerSizes = () => {
    if (lg.value) {
        assetsSizes.joker.width = 61;
        assetsSizes.joker.height = 61;
        assetsSizes.joker.textMarginTop = 24;
        assetsSizes.joker.assetMarginTop = 0;
    } else if (sm2.value) {
        assetsSizes.joker.width = 44;
        assetsSizes.joker.height = 44;
        assetsSizes.joker.textMarginTop = 12;
        assetsSizes.joker.assetMarginTop = 0;
    } else if (xs2.value) {
        assetsSizes.joker.width = 41;
        assetsSizes.joker.height = 41;
        assetsSizes.joker.textMarginTop = 12;
        assetsSizes.joker.assetMarginTop = 0;
    } else if (xs1.value) {
        assetsSizes.joker.width = 34;
        assetsSizes.joker.height = 34;
        assetsSizes.joker.textMarginTop = 12;
        assetsSizes.joker.assetMarginTop = 1;
    } else {
        assetsSizes.joker.width = 26;
        assetsSizes.joker.height = 26;
        assetsSizes.joker.textMarginTop = 10;
        assetsSizes.joker.assetMarginTop = 4;
    }
};

const assets: WheelAssetsConfiguration = {
    wheelConstruction: "/frontend/web/img/golden-wheel/wheel/wheel-construction.png",
    wheelConstructionPointer: "/frontend/web/img/golden-wheel/wheel/wheel-construction-pointer.png",
    wheelPins: "/frontend/web/img/golden-wheel/wheel/wheel-construction-pins.png",
    logo: "/frontend/web/img/logos/sazka-master-brand/logo/sazka-logo-color.svg",
    mainPrize: "/frontend/web/img/golden-wheel/wheel/main-prize.png",
    staticInsideOn: "/frontend/web/img/golden-wheel/wheel/gw-static-inside-on.png",
    staticInsideDisabled: "/frontend/web/img/golden-wheel/wheel/gw-static-inside-disabled.png",
    staticLogo: "/frontend/web/img/menu/logo-sazka.svg?v=2",
    lightsOutside: {
        off: "/frontend/web/img/golden-wheel/wheel/lights-outside/lights-outside-off.png",
        on: "/frontend/web/img/golden-wheel/wheel/lights-outside/lights-outside-on.png",
        activeVideo: {
            webm: "/frontend/web/img/golden-wheel/wheel/lights-outside/lights-outside-active.webm",
            mov: "/frontend/web/img/golden-wheel/wheel/lights-outside/lights-outside-active.mov",
        },
        finishedVideo: {
            webm: "/frontend/web/img/golden-wheel/wheel/lights-outside/lights-outside-finished.webm",
            mov: "/frontend/web/img/golden-wheel/wheel/lights-outside/lights-outside-finished.mov",
        },
    },
    lightsInside: {
        off: "/frontend/web/img/golden-wheel/wheel/lights-inside/lights-inside-off.png",
        on: "/frontend/web/img/golden-wheel/wheel/lights-inside/lights-inside-on.png",
        activationVideo: {
            webm: "/frontend/web/img/golden-wheel/wheel/lights-inside/lights-inside-activation.webm",
            mov: "/frontend/web/img/golden-wheel/wheel/lights-inside/lights-inside-activation.mov",
        },
        finishedVideo: {
            webm: "/frontend/web/img/golden-wheel/wheel/lights-inside/lights-inside-finished.webm",
            mov: "/frontend/web/img/golden-wheel/wheel/lights-inside/lights-inside-finished.mov",
        },
    },
    logoFlipVideo: {
        webm: "/frontend/web/img/golden-wheel/wheel/logo-flip/logo-flip.webm",
        mov: "/frontend/web/img/golden-wheel/wheel/logo-flip/logo-flip.mov",
    },
    buttonGlints: {
        small: "/frontend/web/img/golden-wheel/wheel/gw-spin-btn-glint-sm.png",
        large: "/frontend/web/img/golden-wheel/wheel/gw-spin-btn-glint.png",
    },
    jokerIcon: "/frontend/web/img/golden-wheel/wheel/joker-icon-light-wheel.svg",
};

watchEffect(() => {
    setGwWheelSizes();
    setGwTextSizes();
    setGwJokerSizes();
});

export function useGoldenWheelConfiguration(): CanvasConfiguration {
    return {
        colors,
        wheelSizes,
        textSizes,
        assets,
        assetsSizes,
    };
}
